import * as React from "react";
import {Suspense} from "react";
import {Accordion as BootstrapAccordion, Card, Col, ListGroup, Row} from "react-bootstrap";
import {IncludedWithProduct} from "../components/includedWithProduct";
import {EnvironmentalClaims} from "../components/environmentalClaims";
import {CallOut} from "../components/callOut";
import {ShortDescription} from "../components/shortDescription";
import {LongDescription} from "../components/longDescription";
import {BulletPoints} from "../components/bulletPoints";
import {BrandLogo} from "../../brandLogo";
import {ReplacementParts} from "../components/replacementParts";
import {ArtworkTemplatesAndGuidelines} from "../components/artworkTemplatesAndGuidelines";
import {ProductInfoAndInstallation} from "../components/productInfoAndInstallation";
import {ArticlesAndGuides} from "../components/articlesAndGuides";
import Skeleton from "react-loading-skeleton";
import {useAccordionVariables} from "../../../../../hooks/useAccordionVariables";
import {AccordionItemType} from "../../../../../definitions/enums";
import {ErrorBoundary} from "@sentry/react";
import {useTemplatesAndGuidelinesQuery} from "../../../../../hooks/useTemplatesAndGuidelinesQuery";
import {useProductInfoAndInstallationQuery} from "../../../../../hooks/useProductInfoAndInstallationQuery";
import {useArticlesGuidesQuery} from "../../../../../hooks/useArticlesGuidesQuery";
import {useEnvironmentalClaimsQuery} from "../../../../../hooks/useEnvironmentalClaimsQuery";

export const Details = () => {
    return (
        <ErrorBoundary fallback={<></>}>
            <Header/>
            <Suspense fallback={<SkeletonBody />}>
            <Body/>
            </Suspense>
        </ErrorBoundary>
    )
}

const SkeletonBody = () => {
    return (
        <Row className="gx-5 px-3 my-2">
            <Col md={7} lg={8}>
                <Skeleton/>
                <Skeleton count={5}/>
                <Skeleton/>
                <br/>
                <Skeleton/>
                <Skeleton count={5}/>
                <Skeleton/>
                <br/>
                <Skeleton/>
                <Skeleton count={5}/>
                <Skeleton/>
                <br/>
                <Skeleton/>
                <Skeleton count={4}/>
            </Col>
            <Col md={5} lg={4} className="d-flex flex-column">
                <Card body className="my-3">
                    <SkeletonListGroup/>
                    <SkeletonListGroup/>
                    <SkeletonListGroup/>
                </Card>
            </Col>
        </Row>
    )
}

const SkeletonListGroup = () => (
    <>
        <ListGroup variant="flush">
            <ListGroup.Item>
                <strong>
                    <Skeleton height={30}/>
                </strong>
            </ListGroup.Item>
            <ListGroup.Item>
                <Skeleton/>
            </ListGroup.Item>
            <ListGroup.Item>
                <Skeleton/>
            </ListGroup.Item>
            <ListGroup.Item>
                <Skeleton/>
            </ListGroup.Item>
        </ListGroup>
        <hr/>
    </>
);

const Header = () => {
    const {onClick} = useAccordionVariables();
    return (
        <BootstrapAccordion.Header className="fs-3" onClick={() => onClick(AccordionItemType.ProductDetails)}>
            Product Details
        </BootstrapAccordion.Header>
    );
};

const Body = () => {
    const {model: {claims}} = useEnvironmentalClaimsQuery();
    const templateAndGuidelineModel = useTemplatesAndGuidelinesQuery();
    const productInfoAndInstallationModel = useProductInfoAndInstallationQuery();
    const articleGuideModel = useArticlesGuidesQuery();
    const shouldUseFullWidth = templateAndGuidelineModel.breakOutEarly && !productInfoAndInstallationModel.hasResources && !articleGuideModel.hasResources;

    return (
        <BootstrapAccordion.Body>
            <Suspense fallback={<></>}>
                <Row className="gx-5">
                <Col md={shouldUseFullWidth ? 12 : 7} lg={shouldUseFullWidth ? 12 : 8}>
                        <div className="sticky-wrap">
                            <ShortDescription />
                            <LongDescription />
                            <Row className="gx-5">
                                <Col lg={(!claims || claims.length === 0) ? 12 : 6}>
                                    <IncludedWithProduct />
                                    <ul>
                                        <BulletPoints showViewAllLink={false}/>
                                    </ul>
                                    <BrandLogo />
                                </Col>
                                <EnvironmentalClaims />
                            </Row>
                            <CallOut />
                        </div>
                    </Col>
                    <Col md={5} lg={4} className={`d-flex flex-column details-links-column ps-1 ps-md-0 pe-1 ${shouldUseFullWidth && 'border-0'}`}>
                        <Card className="mb-3 mt-0 pt-0 px-0">
                            <Card.Body className="px-0 py-0">
                                <ReplacementParts />
                                <Resources />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Suspense>
        </BootstrapAccordion.Body>
    )
}

const Resources = () => {
    const templateAndGuidelineModel = useTemplatesAndGuidelinesQuery();
    const productInfoAndInstallationModel = useProductInfoAndInstallationQuery();
    const articleGuideModel = useArticlesGuidesQuery();

    if (templateAndGuidelineModel.breakOutEarly && !productInfoAndInstallationModel.hasResources && !articleGuideModel.hasResources) return <></>;

    return (
        <>
            <ArtworkTemplatesAndGuidelines model={templateAndGuidelineModel} />
            <ProductInfoAndInstallation model={productInfoAndInstallationModel} />
            <ArticlesAndGuides model={articleGuideModel} />
        </>
    );
};